'use client';
/**
 * components/NavigationPending.tsx — the page-load spinner for in-app
 * navigation (speed pass 2026-09-28, replaces app/loading.tsx).
 *
 * WHY NOT app/loading.tsx ANY MORE (SOP-WEBSITE-03 Rule 2):
 * a root loading.tsx wraps EVERY page in a Suspense boundary, and on this
 * stack that boundary also applies to the first, server-rendered page load:
 * the HTML shipped the spinner, then the whole page inside
 * <div hidden id="S:0">, revealed only once the entire document had arrived.
 * Measured 2026-09-28 on /pro-member (Slow 4G, 4× CPU): the hero image was
 * loaded at 0.6 s but painted at 3.1 s — the moment the last byte of HTML
 * landed. Every page, static ones included, carried that delay.
 *
 * WHAT STAYS THE SAME: the moment Lars described ("a page when it's slow") —
 * a click on an internal link whose page takes a perceptible moment shows the
 * same full-screen OrbitSpinner on --bg that loading.tsx showed, over the page
 * chrome, with the help pill and cookie banner still on top as before.
 * Instant navigations (prefetched pages) still show nothing: the spinner waits
 * SHOW_AFTER_MS before appearing, and disappears when the new route renders.
 *
 * Only Next.js <Link> navigations count: next/link calls preventDefault() on
 * the click before this document-level listener runs (React's own listener is
 * registered first, at hydration). A plain <a> is a full page load — the
 * browser keeps showing the old page, exactly as before.
 */
import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import OrbitSpinner from '@/components/OrbitSpinner';

const SHOW_AFTER_MS = 150;
const GIVE_UP_AFTER_MS = 15000;

export default function NavigationPending() {
  const pathname = usePathname();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let showTimer: ReturnType<typeof setTimeout> | null = null;
    let giveUpTimer: ReturnType<typeof setTimeout> | null = null;
    const clear = () => {
      if (showTimer) clearTimeout(showTimer);
      if (giveUpTimer) clearTimeout(giveUpTimer);
      showTimer = giveUpTimer = null;
    };

    const onClick = (e: MouseEvent) => {
      if (!e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a || (a.target && a.target !== '_self') || a.hasAttribute('download')) return;
      let url: URL;
      try {
        url = new URL(a.href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin || url.pathname === window.location.pathname) return;
      clear();
      showTimer = setTimeout(() => setVisible(true), SHOW_AFTER_MS);
      giveUpTimer = setTimeout(() => {
        clear();
        setVisible(false);
      }, GIVE_UP_AFTER_MS);
    };

    document.addEventListener('click', onClick);
    (window as unknown as { __pfNavPendingClear?: () => void }).__pfNavPendingClear = clear;
    return () => {
      document.removeEventListener('click', onClick);
      clear();
    };
  }, []);

  // The new route rendered: hide, and cancel a spinner that has not shown yet.
  useEffect(() => {
    (window as unknown as { __pfNavPendingClear?: () => void }).__pfNavPendingClear?.();
    setVisible(false);
  }, [pathname]);

  if (!visible) return null;
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 890, // above page chrome, below the help pill (900) — as loading.tsx left it
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg)',
      }}
    >
      <OrbitSpinner size={56} aria-label="Loading page" />
    </div>
  );
}
